// src/lib/api_links.ts — „API-uri definibile prin configurare" (F7-T4, REQ-API-004,
// IT-API-03/10/21/23/25): operatorul definește pe HARTĂ (EAV `attributes.api_links`, scris prin
// PATCH-ul generic — ca `legislatie`) linkuri către sisteme EXTERNE, cu placeholders completați
// din elementul selectat: `{lng}`, `{lat}`, `{attr:<cheie>}`. „Interogarea altor sisteme pe baza
// parametrilor extrași din hartă, fără dezvoltări software" = un URL-șablon + un click.
//
// Funcții PURE, partajate server↔client (ca lib/rlu.ts) — zero dependențe. Parsarea e DEFENSIVĂ
// (EAV-ul e schemaless), iar expandarea e FAIL-CLOSED pe scheme nesigure (doar http/https —
// anti-`javascript:`; garda-soră: `safe_stema_url` din public.ts).

export interface ApiLink {
	label: string
	url_template: string
	/** Slug-urile straturilor pe care apare linkul; gol = pe toate. */
	layers: string[]
}

/** `attributes.api_links` (unknown din EAV) → listă tipată. Formă neașteptată → degradare tăcută. */
export function parse_api_links(v: unknown): ApiLink[] {
	let raw = v
	if (typeof raw === "string") {
		try {
			raw = JSON.parse(raw)
		} catch {
			return []
		}
	}
	if (!Array.isArray(raw)) {
		return []
	}
	const out: ApiLink[] = []
	for (const item of raw) {
		if (typeof item !== "object" || item === null) {
			continue
		}
		const o = item as Record<string, unknown>
		const label = typeof o.label === "string" ? o.label.trim() : ""
		const url_template = typeof o.url_template === "string" ? o.url_template.trim() : ""
		if (!label || !url_template) {
			continue
		}
		out.push({
			label,
			url_template,
			layers: Array.isArray(o.layers)
				? o.layers.filter((s): s is string => typeof s === "string" && s !== "")
				: [],
		})
	}
	return out
}

/** Contextul de expandare: punctul-ancoră + atributele elementului selectat (deja F10-filtrate
 *  pe calea publică — properties vin filtrate de server, deci substituția nu poate scurge nimic). */
export interface ExpandCtx {
	lng: number
	lat: number
	props: Record<string, unknown>
}

const PLACEHOLDER_RE = /\{(lng|lat|attr:([^}]+))\}/g

/**
 * Expandarea URL-șablonului: `{lng}`/`{lat}` → coordonatele ancorei, `{attr:cheie}` → valoarea
 * atributului (gol dacă lipsește — linkul rămâne valid, sistemul extern decide). TOATE valorile
 * trec prin `encodeURIComponent`. `""` = șablon inutilizabil (schemă nesigură) — apelantul nu
 * randează linkul.
 */
export function expand_url_template(tpl: string, ctx: ExpandCtx): string {
	if (!/^https?:\/\//i.test(tpl.trim())) {
		return "" // fail-closed: doar http(s) — un șablon `javascript:` nu devine niciodată <a href>
	}
	return tpl.trim().replace(PLACEHOLDER_RE, (_m, kind: string, attr_key?: string) => {
		if (kind === "lng") {
			return encodeURIComponent(String(ctx.lng))
		}
		if (kind === "lat") {
			return encodeURIComponent(String(ctx.lat))
		}
		const v = ctx.props[(attr_key ?? "").trim()]
		return v === null || v === undefined ? "" : encodeURIComponent(String(v))
	})
}

/**
 * Punct-ancoră al unei geometrii GeoJSON oarecare: media tuturor pozițiilor (recursiv) — suficient
 * pentru pre-completarea unui sistem extern (nu e măsurătoare). [0,0] = geometrie inutilizabilă.
 */
export function anchor_point(
	geometry: { type?: string; coordinates?: unknown } | null | undefined,
): [number, number] {
	let sx = 0
	let sy = 0
	let n = 0
	const walk = (c: unknown): void => {
		if (!Array.isArray(c)) {
			return
		}
		if (c.length >= 2 && typeof c[0] === "number" && typeof c[1] === "number") {
			sx += c[0]
			sy += c[1]
			n += 1
			return
		}
		for (const child of c) {
			walk(child)
		}
	}
	walk(geometry?.coordinates)
	return n === 0 ? [0, 0] : [sx / n, sy / n]
}
